import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Logger from "../../../services/Logger";
import StatsJigsawService from "../../../services/StatsJigsawService";
import StatsSummaryTile from "../../pzl_common/stats/StatsSummaryTile";

dayjs.extend(isoWeek);

const PERIOD_OPTIONS = [
    { value: "all", label: "All" },
    { value: "month", label: "This month" },
    { value: "week", label: "This week" },
];

const DEFAULT_PERIOD = "all";

function periodBounds(period) {
    const end = dayjs();
    if (period === "week") {
        return { start: end.startOf("isoWeek"), end };
    }
    if (period === "month") {
        return { start: end.startOf("month"), end };
    }
    return { start: dayjs("2000-01-01"), end };
}

function loadStoredBool(key, fallback) {
    if (!key) return fallback;
    try {
        const stored = window.localStorage.getItem(key);
        if (stored === "true") return true;
        if (stored === "false") return false;
    } catch {
        /* private mode */
    }
    return fallback;
}

function persistStoredBool(key, value) {
    if (!key) return;
    try {
        window.localStorage.setItem(key, value ? "true" : "false");
    } catch {
        /* private mode */
    }
}

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatPercent = (value) =>
    Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : "—";

function offeredBasketsLabel(row) {
    if (!row?.hasBaskets) {
        return "None";
    }
    const minK = Number(row.minBasketCount) || 0;
    const maxK = Number(row.maxBasketCount) || 0;
    const free = Number(row.freeBasketCount) || 0;
    if (minK === maxK) {
        return `${maxK} (${free} free)`;
    }
    return `${minK}–${maxK} (${free} free)`;
}

function readStoredPeriod(key, fallback = DEFAULT_PERIOD) {
    try {
        const stored = window.localStorage.getItem(key);
        return PERIOD_OPTIONS.some((option) => option.value === stored) ? stored : fallback;
    } catch {
        return fallback;
    }
}

function persistPeriod(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* private mode */
    }
}

function periodHintText(period) {
    if (period === "week") {
        return "This week is Monday through today.";
    }
    if (period === "month") {
        return "This month is the 1st through today.";
    }
    return "All recorded activity.";
}

function PeriodSelect({ id, value, onChange }) {
    return (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id={id}>Period</InputLabel>
            <Select
                labelId={id}
                label="Period"
                value={value}
                onChange={(event) => onChange(event.target.value)}
            >
                {PERIOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

/**
 * Color-basket usage for jigsaw: complete ratio per basket and size, free vs paid,
 * All / this month / this week. 6×6 boards do not offer baskets.
 */
export default function JigsawBasketStatsPanel({
    language,
    periodStorageKey = "admin.jigsaw.basketStats.period.v2",
    sizePeriodStorageKey = "admin.jigsaw.basketStats.sizePeriod",
    kPeriodStorageKey = "admin.jigsaw.basketStats.kPeriod",
    excludeTestStorageKey = "admin.jigsaw.basketStats.excludeTest",
    excludeStaffStorageKey = "admin.jigsaw.basketStats.excludeStaff",
}) {
    const [period, setPeriod] = useState(() => readStoredPeriod(periodStorageKey));
    const [sizePeriod, setSizePeriod] = useState(() => readStoredPeriod(sizePeriodStorageKey));
    const [kPeriod, setKPeriod] = useState(() => readStoredPeriod(kPeriodStorageKey));
    const [excludeTest, setExcludeTest] = useState(() => loadStoredBool(excludeTestStorageKey, true));
    const [excludeStaff, setExcludeStaff] = useState(() => loadStoredBool(excludeStaffStorageKey, true));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [sizeLoading, setSizeLoading] = useState(true);
    const [sizeError, setSizeError] = useState(null);
    const [sizeStats, setSizeStats] = useState(null);
    const [kLoading, setKLoading] = useState(true);
    const [kError, setKError] = useState(null);
    const [kStats, setKStats] = useState(null);

    const handlePeriodChange = useCallback(
        (value) => {
            setPeriod(value);
            persistPeriod(periodStorageKey, value);
        },
        [periodStorageKey]
    );

    const handleSizePeriodChange = useCallback(
        (value) => {
            setSizePeriod(value);
            persistPeriod(sizePeriodStorageKey, value);
        },
        [sizePeriodStorageKey]
    );

    const handleKPeriodChange = useCallback(
        (value) => {
            setKPeriod(value);
            persistPeriod(kPeriodStorageKey, value);
        },
        [kPeriodStorageKey]
    );

    const fetchParams = useCallback(
        (selectedPeriod) => {
            const { start, end } = periodBounds(selectedPeriod);
            return {
                start: start.unix(),
                end: end.unix(),
                language,
                includeTest: !excludeTest,
                excludeStaff,
            };
        },
        [language, excludeTest, excludeStaff]
    );

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await StatsJigsawService.colorBasketStats(fetchParams(period));
                if (!cancelled) {
                    setStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load jigsaw basket stats:", err);
                if (!cancelled) {
                    setError("Failed to load basket statistics.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchParams, period]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setSizeLoading(true);
            setSizeError(null);
            try {
                const result = await StatsJigsawService.colorBasketStats(fetchParams(sizePeriod));
                if (!cancelled) {
                    setSizeStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load jigsaw basket size stats:", err);
                if (!cancelled) {
                    setSizeError("Failed to load usage by puzzle size.");
                    setSizeStats(null);
                }
            } finally {
                if (!cancelled) {
                    setSizeLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchParams, sizePeriod]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setKLoading(true);
            setKError(null);
            try {
                const result = await StatsJigsawService.colorBasketStats(fetchParams(kPeriod));
                if (!cancelled) {
                    setKStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load jigsaw basket K stats:", err);
                if (!cancelled) {
                    setKError("Failed to load sessions by basket count.");
                    setKStats(null);
                }
            } finally {
                if (!cancelled) {
                    setKLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchParams, kPeriod]);

    const totals = stats?.totals ?? {};
    const freeMaxCount = Number(stats?.freeMaxCount ?? sizeStats?.freeMaxCount ?? kStats?.freeMaxCount) || 2;
    const byBasketIndex = useMemo(
        () => (Array.isArray(stats?.byBasketIndex) ? stats.byBasketIndex : []),
        [stats]
    );
    const byTileSize = useMemo(
        () => (Array.isArray(sizeStats?.byTileSize) ? sizeStats.byTileSize : []),
        [sizeStats]
    );
    const byBasketCount = useMemo(
        () => (Array.isArray(kStats?.byBasketCount) ? kStats.byBasketCount : []),
        [kStats]
    );

    const periodHint = periodHintText(period);

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="jigsaw-basket-stats-period-label">Period</InputLabel>
                    <Select
                        labelId="jigsaw-basket-stats-period-label"
                        label="Period"
                        value={period}
                        onChange={(event) => handlePeriodChange(event.target.value)}
                    >
                        {PERIOD_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) => {
                                const checked = event.target.checked;
                                setExcludeTest(checked);
                                persistStoredBool(excludeTestStorageKey, checked);
                            }}
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) => {
                                const checked = event.target.checked;
                                setExcludeStaff(checked);
                                persistStoredBool(excludeStaffStorageKey, checked);
                            }}
                        />
                    }
                    label="Exclude staff"
                />

                <Typography variant="body2" color="text.secondary">
                    {language} · signed-in players
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                    jigsaw_color_basket
                </Box>{" "}
                is the precomputed catalog (one row per board × K), not player usage. Sessions are in{" "}
                <Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.85em" }}>
                    jigsaw_color_basket_play
                </Box>
                . First {freeMaxCount} baskets are free; 6×6 has none. {periodHint}
            </Typography>

            {loading && !stats && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {stats && (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: 1,
                            mb: 3,
                        }}
                    >
                        <StatsSummaryTile
                            label="Catalog rows"
                            value={formatNumber(totals.catalogRows)}
                            hint={`${formatNumber(totals.catalogBoards)} boards · ${formatNumber(totals.catalogSlots)} color groups`}
                        />
                        <StatsSummaryTile
                            label="Basket sessions"
                            value={formatNumber(totals.sessions)}
                            hint={`${formatNumber(totals.players)} players in this period`}
                        />
                        <StatsSummaryTile
                            label="Completed"
                            value={formatNumber(totals.completed)}
                            hint={formatPercent(totals.completionRate)}
                        />
                        <StatsSummaryTile
                            label="Free baskets"
                            value={formatNumber(totals.freeSessions)}
                            hint={`${formatPercent(totals.freeCompletionRate)} complete`}
                        />
                        <StatsSummaryTile
                            label="Paid baskets"
                            value={formatNumber(totals.paidSessions)}
                            hint={`${formatPercent(totals.paidCompletionRate)} complete`}
                        />
                        <StatsSummaryTile
                            label="Usage on eligible sizes"
                            value={formatPercent(totals.usageRate)}
                            hint={`${formatNumber(totals.players)} / ${formatNumber(totals.eligiblePlayers)} players`}
                        />
                        <StatsSummaryTile
                            label="Coin unlocks"
                            value={formatNumber(totals.paidUnlocks)}
                            hint={`${formatNumber(totals.coinsSpent)} coins · ${formatNumber(totals.unlockPlayers)} players`}
                        />
                    </Box>

                    {Number(totals.sessions ?? 0) === 0 && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            No color-basket sessions in this period for {language}
                            {excludeTest ? " (test accounts excluded)" : ""}
                            {excludeStaff ? " (staff excluded)" : ""}.
                        </Alert>
                    )}

                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                Complete ratio per basket
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Basket 1–{freeMaxCount} are free. Complete means the basket was solved and
                                deployed onto the main board.
                            </Typography>
                            {byBasketIndex.length > 0 ? (
                                <>
                                    <BarChart
                                        height={320}
                                        xAxis={[
                                            {
                                                scaleType: "band",
                                                data: byBasketIndex.map(
                                                    (row) =>
                                                        `${row.label || `Basket ${Number(row.basketIndex) + 1}`}${
                                                            row.access === "paid" ? " · paid" : " · free"
                                                        }`
                                                ),
                                            },
                                        ]}
                                        series={[
                                            {
                                                data: byBasketIndex.map((row) => Number(row.sessions) || 0),
                                                label: "Opened",
                                                color: "#1976d2",
                                            },
                                            {
                                                data: byBasketIndex.map((row) => Number(row.completed) || 0),
                                                label: "Completed",
                                                color: "#3BA666",
                                            },
                                        ]}
                                    />
                                    <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
                                        <Table size="small" sx={{ minWidth: 520 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>Basket</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Opened
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Completed
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Complete %
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Players
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {byBasketIndex.map((row) => (
                                                    <TableRow key={row.basketIndex}>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {row.label || `Basket ${Number(row.basketIndex) + 1}`}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.access === "paid" ? "Paid" : "Free"}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.sessions)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.completed)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatPercent(row.completionRate)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.players)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : (
                                <Alert severity="info">No basket-index activity in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Usage by puzzle size
                                </Typography>
                                <PeriodSelect
                                    id="jigsaw-basket-size-period-label"
                                    value={sizePeriod}
                                    onChange={handleSizePeriodChange}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Catalog columns are all-time rows in jigsaw_color_basket. Plays and sessions
                                follow the period on this card. {periodHintText(sizePeriod)} Usage is
                                signed-in players who opened a basket versus signed-in players of that size.
                            </Typography>
                            {sizeLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : sizeError ? (
                                <Alert severity="error">{sizeError}</Alert>
                            ) : byTileSize.length > 0 ? (
                                <>
                                    <BarChart
                                        height={320}
                                        xAxis={[
                                            {
                                                scaleType: "band",
                                                data: byTileSize.map((row) => row.band || `${row.tileSize}×${row.tileSize}`),
                                            },
                                        ]}
                                        series={[
                                            {
                                                data: byTileSize.map((row) => Number(row.plays) || 0),
                                                label: "Plays (incl. guests)",
                                                color: "#7b1fa2",
                                            },
                                            {
                                                data: byTileSize.map((row) => Number(row.sessions) || 0),
                                                label: "Basket sessions",
                                                color: "#1976d2",
                                            },
                                            {
                                                data: byTileSize.map((row) => Number(row.completed) || 0),
                                                label: "Baskets completed",
                                                color: "#3BA666",
                                            },
                                        ]}
                                    />
                                    <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
                                        <Table size="small" stickyHeader sx={{ minWidth: 920 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Baskets offered</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Catalog rows
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Catalog boards
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Published
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Plays
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Guests
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Basket sessions
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Complete %
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Usage %
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        Free / paid
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {byTileSize.map((row) => (
                                                    <TableRow key={row.tileSize}>
                                                        <TableCell sx={{ fontWeight: 600 }}>
                                                            {row.band || `${row.tileSize}×${row.tileSize}`}
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {formatNumber(row.pieces)} pieces
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{offeredBasketsLabel(row)}</TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.catalogRows)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.catalogBoards)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.publishedBoards)}
                                                        </TableCell>
                                                        <TableCell align="right">{formatNumber(row.plays)}</TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.guestPlays)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(row.sessions)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.hasBaskets && Number(row.sessions) > 0
                                                                ? formatPercent(row.completionRate)
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.hasBaskets && Number(row.players) > 0
                                                                ? formatPercent(row.usageRate)
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.hasBaskets
                                                                ? `${formatNumber(row.freeSessions)} / ${formatNumber(row.paidSessions)}`
                                                                : "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : (
                                <Alert severity="info">No jigsaw plays in this period to group by size.</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                    Sessions by basket count (K)
                                </Typography>
                                <PeriodSelect
                                    id="jigsaw-basket-k-period-label"
                                    value={kPeriod}
                                    onChange={handleKPeriodChange}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Catalog is all-time. Opened / completed follow the period on this card.{" "}
                                {periodHintText(kPeriod)}
                            </Typography>
                            {kLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : kError ? (
                                <Alert severity="error">{kError}</Alert>
                            ) : byBasketCount.length > 0 ? (
                                <TableContainer sx={{ overflowX: "auto" }}>
                                    <Table size="small" sx={{ minWidth: 640 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Baskets (K)</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Catalog rows
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Catalog boards
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Opened
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Completed
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Complete %
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    Players
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {byBasketCount.map((row) => (
                                                <TableRow key={row.basketCount}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.basketCount}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.catalogRows)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.catalogBoards)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.sessions)}</TableCell>
                                                    <TableCell align="right">{formatNumber(row.completed)}</TableCell>
                                                    <TableCell align="right">
                                                        {Number(row.sessions) > 0 ? formatPercent(row.completionRate) : "—"}
                                                    </TableCell>
                                                    <TableCell align="right">{formatNumber(row.players)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">No K-level activity in this period.</Alert>
                            )}
                        </CardContent>
                    </Card>
        </Box>
    );
}
